import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { FixedSizeList as List } from "react-window";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Search,
  ZoomIn,
  ZoomOut,
  RotateCw,
  WrapText,
  Copy,
  Download,
  AlertCircle,
} from "lucide-react";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";

interface TextViewerProps {
  signedUrl: string;
  fileName?: string;
  elementId?: string;
}

const TextViewer: React.FC<TextViewerProps> = ({
  signedUrl,
  fileName,
  elementId,
}) => {
  const [lines, setLines] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState(14);
  const [wordWrap, setWordWrap] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchingLines, setMatchingLines] = useState<Set<number>>(new Set());
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [lineHeight, setLineHeight] = useState(20);
  const [citationLineNumber, setCitationLineNumber] = useState<number | null>(
    null,
  );
  const [citationSnippet, setCitationSnippet] = useState<string | null>(null);
  const listRef = useRef<List>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textContentRef = useRef<string>("");

  // Parse elementId for citation navigation
  const parseElementId = (elementId: string) => {
    if (!elementId) return null;

    // Format: line_{line_number}#{text_snippet}
    const match = elementId.match(/^line_(\d+)#(.+)$/);
    if (!match) return null;

    const lineNumber = parseInt(match[1], 10);
    const textSnippet = match[2];

    return { lineNumber, textSnippet };
  };

  // Handle citation navigation when elementId changes
  useEffect(() => {
    if (elementId && lines.length > 0) {
      const parsed = parseElementId(elementId);
      if (parsed) {
        const { lineNumber, textSnippet } = parsed;
        setCitationLineNumber(lineNumber);
        setCitationSnippet(textSnippet);

        // Navigate to the line (convert to 0-based index)
        const lineIndex = lineNumber - 1;
        if (lineIndex >= 0 && lineIndex < lines.length) {
          setTimeout(() => {
            listRef.current?.scrollToItem(lineIndex, "center");
          }, 100);
        }
      }
    } else {
      setCitationLineNumber(null);
      setCitationSnippet(null);
    }
  }, [elementId, lines.length]);

  // Load file in chunks
  useEffect(() => {
    const loadFile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(signedUrl);
        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.statusText}`);
        }

        // For large files, we'll stream the content
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("Unable to read file stream");
        }

        const decoder = new TextDecoder();
        let buffer = "";
        const lineArray: string[] = [];
        let done = false;

        while (!done) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;

          if (value) {
            buffer += decoder.decode(value, { stream: !done });

            // Process complete lines
            const lines = buffer.split("\n");

            // Keep the last incomplete line in buffer
            if (!done) {
              buffer = lines.pop() || "";
            } else {
              buffer = "";
            }

            lineArray.push(...lines);

            // Update state periodically for large files
            if (lineArray.length % 10000 === 0) {
              setLines([...lineArray]);
            }
          }
        }

        // Add any remaining content
        if (buffer) {
          lineArray.push(buffer);
        }

        textContentRef.current = lineArray.join("\n");
        setLines(lineArray);
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading text file:", err);
        setError(err instanceof Error ? err.message : "Failed to load file");
        setIsLoading(false);
      }
    };

    loadFile();
  }, [signedUrl]);

  // Update line height when font size changes
  useEffect(() => {
    setLineHeight(fontSize * 1.5);
  }, [fontSize]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setMatchingLines(new Set());
      setTotalMatches(0);
      setCurrentMatchIndex(0);
      return;
    }

    const matches = new Set<number>();
    const query = searchQuery.toLowerCase();

    lines.forEach((line, index) => {
      if (line.toLowerCase().includes(query)) {
        matches.add(index);
      }
    });

    setMatchingLines(matches);
    setTotalMatches(matches.size);
    setCurrentMatchIndex(0);
  }, [searchQuery, lines]);

  // Navigate to next match
  const goToNextMatch = useCallback(() => {
    if (matchingLines.size === 0) return;

    const matchArray = Array.from(matchingLines).sort((a, b) => a - b);
    const nextIndex = (currentMatchIndex + 1) % matchArray.length;
    setCurrentMatchIndex(nextIndex);
    listRef.current?.scrollToItem(matchArray[nextIndex], "center");
  }, [matchingLines, currentMatchIndex]);

  // Navigate to previous match
  const goToPreviousMatch = useCallback(() => {
    if (matchingLines.size === 0) return;

    const matchArray = Array.from(matchingLines).sort((a, b) => a - b);
    const prevIndex =
      currentMatchIndex === 0 ? matchArray.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    listRef.current?.scrollToItem(matchArray[prevIndex], "center");
  }, [matchingLines, currentMatchIndex]);

  // Zoom controls
  const handleZoomIn = () => setFontSize((prev) => Math.min(prev + 2, 32));
  const handleZoomOut = () => setFontSize((prev) => Math.max(prev - 2, 8));
  const handleResetZoom = () => setFontSize(14);

  // Copy to clipboard
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(textContentRef.current);
  }, []);

  // Download file
  const handleDownload = useCallback(() => {
    const blob = new Blob([textContentRef.current], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "document.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [fileName]);

  // Row renderer for virtualized list
  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const line = lines[index];
    const isMatch = matchingLines.has(index);
    const lineNumber = index + 1;
    const isCitationLine = citationLineNumber === lineNumber;

    // Highlight search matches or citation snippet in the line
    const highlightedContent = useMemo(() => {
      // Citation highlighting takes precedence
      if (isCitationLine && citationSnippet) {
        const snippetIndex = line.indexOf(citationSnippet);
        if (snippetIndex !== -1) {
          const parts: React.ReactNode[] = [];

          // Add text before citation
          if (snippetIndex > 0) {
            parts.push(line.substring(0, snippetIndex));
          }

          // Add highlighted citation
          parts.push(
            <mark
              key={`citation-${index}`}
              className="bg-blue-300 border-b-2 border-blue-600"
            >
              {citationSnippet}
            </mark>,
          );

          // Add text after citation
          const endIndex = snippetIndex + citationSnippet.length;
          if (endIndex < line.length) {
            parts.push(line.substring(endIndex));
          }

          return <>{parts}</>;
        }
      }

      // Search highlighting
      if (searchQuery.trim() && isMatch) {
        const query = searchQuery.toLowerCase();
        const lowerLine = line.toLowerCase();
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let matchIndex = lowerLine.indexOf(query);

        while (matchIndex !== -1) {
          // Add text before match
          if (matchIndex > lastIndex) {
            parts.push(line.substring(lastIndex, matchIndex));
          }
          // Add highlighted match
          parts.push(
            <mark key={`${index}-${matchIndex}`} className="bg-yellow-300">
              {line.substring(matchIndex, matchIndex + query.length)}
            </mark>,
          );
          lastIndex = matchIndex + query.length;
          matchIndex = lowerLine.indexOf(query, lastIndex);
        }

        // Add remaining text
        if (lastIndex < line.length) {
          parts.push(line.substring(lastIndex));
        }

        return <>{parts}</>;
      }

      return line;
    }, [line, isMatch, index, isCitationLine]);

    return (
      <div
        style={{
          ...style,
          fontSize: `${fontSize}px`,
          lineHeight: `${lineHeight}px`,
        }}
        className={`flex hover:bg-stone-100 ${
          isCitationLine
            ? "bg-blue-50 border-l-4 border-blue-600"
            : isMatch
              ? "bg-yellow-50"
              : ""
        }`}
      >
        <span className="flex-shrink-0 w-16 text-right pr-4 text-stone-400 select-none border-r border-stone-200">
          {lineNumber}
        </span>
        <pre
          className={`flex-1 px-4 m-0 font-mono ${wordWrap ? "whitespace-pre-wrap" : "whitespace-pre"} overflow-hidden`}
        >
          {highlightedContent}
        </pre>
      </div>
    );
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-2">
          <LoadingSpinner />
          <span className="text-sm text-gray-700">Loading file...</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col bg-white">
      {/* Citation Indicator */}
      {citationLineNumber && citationSnippet && (
        <div className="bg-blue-50 border-b border-blue-200 p-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-blue-900">
                Citation Location
              </div>
              <div className="text-xs text-blue-700 font-mono mt-1">
                Line {citationLineNumber}: "
                {citationSnippet.length > 100
                  ? citationSnippet.substring(0, 100) + "..."
                  : citationSnippet}
                "
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b border-stone-200 bg-stone-50">
        {/* Search */}
        <div className="flex items-center gap-1 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Search in file..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4"
            />
          </div>
          {totalMatches > 0 && (
            <>
              <span className="text-sm text-stone-600 whitespace-nowrap">
                {currentMatchIndex + 1} / {totalMatches}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousMatch}
                title="Previous match"
              >
                ↑
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextMatch}
                title="Next match"
              >
                ↓
              </Button>
            </>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomOut}
            title="Decrease font size"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-stone-600 w-12 text-center">
            {fontSize}px
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomIn}
            title="Increase font size"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleResetZoom}
            title="Reset font size"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant={wordWrap ? "default" : "outline"}
            size="icon"
            onClick={() => setWordWrap(!wordWrap)}
            title="Toggle word wrap"
          >
            <WrapText className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleDownload}
            title="Download file"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>

        {/* File info */}
        <div className="text-sm text-stone-600 whitespace-nowrap">
          {lines.length.toLocaleString()} lines
        </div>
      </div>

      {/* Virtualized content */}
      <div className="flex-1 overflow-hidden">
        <List
          ref={listRef}
          height={
            containerRef.current?.clientHeight
              ? containerRef.current.clientHeight - 56
              : 600
          }
          itemCount={lines.length}
          itemSize={lineHeight}
          width="100%"
          className="font-mono"
        >
          {Row}
        </List>
      </div>
    </div>
  );
};

export default TextViewer;
